// Customer complaint list — shows all complaints (support_tickets where is_complaint=true).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle, Inbox, Sparkles, BookOpen, Clock, ChevronRight } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";

const STATUS_TONE = {
    open: "bg-emerald-100 text-emerald-700",
    awaiting_agent: "bg-blue-100 text-blue-700",
    awaiting_customer: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-700",
    resolved: "bg-zinc-100 text-zinc-700",
    closed: "bg-zinc-200 text-zinc-600",
};

const SEVERITY_TONE = {
    low: "border-slate-200 bg-slate-50 text-slate-700",
    medium: "border-amber-200 bg-amber-50 text-amber-800",
    high: "border-orange-200 bg-orange-50 text-orange-800",
    critical: "border-rose-300 bg-rose-50 text-rose-800",
};

const ComplaintsContent = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let m = true;
        (async () => {
            try {
                const { data } = await api.get("/complaints");
                if (m) setItems(data?.items || []);
            } finally {
                if (m) setLoading(false);
            }
        })();
        return () => { m = false; };
    }, []);

    if (loading) {
        return (
            <div className="space-y-3">
                <div className="h-24 skeleton rounded-2xl" />
                <div className="h-24 skeleton rounded-2xl" />
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <EmptyState
                icon={<AlertTriangle className="h-10 w-10 text-brand-600" />}
                title="No complaints — that's good!"
                description="If something went wrong with an order, refund, or delivery, raise a complaint and our AI will help fix it fast."
                action={
                    <Link to="/complaints/new" className="btn-brand" data-testid="open-complaint-btn">
                        <Plus className="h-4 w-4" /> New complaint
                    </Link>
                }
            />
        );
    }

    return (
        <div className="space-y-3" data-testid="complaints-list">
            {items.map((c) => {
                const sev = c.severity_admin || c.severity_auto || "medium";
                return (
                    <Link
                        to={`/complaints/${c.id}`}
                        key={c.id}
                        className="block rounded-2xl border border-zinc-100 bg-white p-5 transition hover:border-brand-200 hover:shadow-sm"
                        data-testid={`complaint-${c.id}`}
                    >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono text-xs text-zinc-400">{c.ticket_no}</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[c.status] || "bg-zinc-100 text-zinc-700"}`}>
                                        {(c.status || "").replace("_", " ")}
                                    </span>
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_TONE[sev]}`}>
                                        {sev}
                                    </span>
                                    {c.complaint_subtype && (
                                        <span className="rounded-full bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
                                            {c.complaint_subtype.replace(/_/g, " ")}
                                        </span>
                                    )}
                                </div>
                                <h3 className="mt-1.5 line-clamp-1 text-base font-semibold text-zinc-900">{c.subject}</h3>
                                <p className="mt-1 text-xs text-zinc-500">
                                    <Clock className="mr-1 inline h-3 w-3" />
                                    {new Date(c.updated_at).toLocaleString()}
                                </p>
                            </div>
                            <ChevronRight className="h-5 w-5 shrink-0 text-zinc-400" />
                        </div>
                    </Link>
                );
            })}
        </div>
    );
};

const Complaints = () => (
    <Layout>
        <section className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
            <header className="mb-8 flex items-end justify-between">
                <div>
                    <p className="label-uppercase">Help</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">My complaints</h1>
                    <p className="mt-1 text-sm text-zinc-500">Track resolution of issues you&apos;ve reported.</p>
                </div>
                <Link to="/complaints/new" className="btn-brand !py-2 !px-4 !text-sm" data-testid="new-complaint-btn">
                    <Plus className="h-4 w-4" /> New complaint
                </Link>
            </header>

            {/* Quick links */}
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Link to="/help/kb" className="group flex items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-4 transition hover:border-brand-200 hover:shadow-sm" data-testid="complaints-link-kb">
                    <BookOpen className="h-5 w-5 shrink-0 text-brand-700" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-zinc-900">Browse help center</p>
                        <p className="text-xs text-zinc-500">Self-help articles for common issues</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-400 transition group-hover:translate-x-1" />
                </Link>
                <Link to="/complaints/new" className="group flex items-center gap-3 rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-violet-50 p-4 transition hover:border-brand-300 hover:shadow-sm" data-testid="complaints-link-wizard">
                    <Sparkles className="h-5 w-5 shrink-0 text-brand-700" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-zinc-900">AI-guided complaint wizard</p>
                        <p className="text-xs text-zinc-600">Get an instant fix or escalate</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-400 transition group-hover:translate-x-1" />
                </Link>
            </div>

            <AuthGuard>
                <ComplaintsContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Complaints;
